import './FlipList.css';
import type { ComponentChildren, RefObject } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useReducedMotion } from '../a11y/useReducedMotion';
import { isTabHidden } from './tween';

export type FlipKey = string | number;

/** A07 (§3.6.3): item que muda de lugar desliza em 450 ms (`--ease-io`). */
export const FLIP_MOVE_MS = 450;
/** A07: item novo entra em 420 ms (`--ease-out`), com 100 ms de atraso, de `translateY(8px)` e opacidade 0. */
export const FLIP_ENTER_MS = 420;
export const FLIP_ENTER_DELAY_MS = 100;
export const FLIP_ENTER_OFFSET_PX = 8;

export interface FlipListProps<T extends { key: FlipKey }> {
  /** Itens com `key` estável (código da UF, id do evento, ISO do país; §15.2 item 4). */
  items: readonly T[];
  /** Conteúdo de cada item; `index` é a posição exibida. */
  renderItem: (item: T, index: number) => ComponentChildren;
  /** Elemento da lista: `ol`/`ul` (itens `<li>`) ou `div` (grade; itens `<div>`). */
  as?: 'ol' | 'ul' | 'div';
  /** Congela a ordem exibida enquanto o ponteiro está sobre a lista ou o foco está dentro dela (§3.9). */
  freezeOnHover?: boolean;
  /** Replay: reordena sem animação (§7.15). */
  replay?: boolean;
  /** Para onde vai o foco se o item focado sair da lista: o título da lista, com `tabindex="-1"` (§15.2 item 4). */
  focusFallback?: RefObject<HTMLElement | null>;
  /** Classes extras do dono (grade, lista). */
  class?: string;
  /** Classes extras de cada item. */
  itemClass?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Ordem exibida durante o congelamento (§3.9): as chaves que continuam na lista mantêm a ordem congelada; as que
 * saíram somem; as novas entram na posição que têm na lista nova (limitada ao fim).
 */
export function mergeFrozenOrder(frozen: readonly FlipKey[], next: readonly FlipKey[]): FlipKey[] {
  const nextSet = new Set(next);
  const out = frozen.filter((k) => nextSet.has(k));
  const placed = new Set(out);
  next.forEach((k, i) => {
    if (placed.has(k)) return;
    out.splice(Math.min(i, out.length), 0, k);
    placed.add(k);
  });
  return out;
}

const sameOrder = (a: readonly FlipKey[], b: readonly FlipKey[]): boolean =>
  a.length === b.length && a.every((k, i) => k === b[i]);

/** Posição de cada item em relação à lista (a rolagem do `ScrollArea` e o deslocamento da página não contam). */
function positions(list: HTMLElement): Map<string, Point> {
  const base = list.getBoundingClientRect();
  const out = new Map<string, Point>();
  for (const el of Array.from(list.children) as HTMLElement[]) {
    const key = el.dataset.key;
    if (key === undefined) continue;
    const r = el.getBoundingClientRect();
    out.set(key, { x: r.left - base.left, y: r.top - base.top });
  }
  return out;
}

/** Curva do token (§3.6.1); a Web Animations não lê var(--…). */
function easing(el: Element, token: string, fallback: string): string {
  const v = typeof getComputedStyle === 'function' ? getComputedStyle(el).getPropertyValue(token).trim() : '';
  return v || fallback;
}

/**
 * Lista que reordena com animação (§3.9, A07): os itens são os mesmos nós do DOM (chave estável), medidos antes e
 * depois de cada mudança de ordem (FLIP); só `transform` e `opacity` animam (§3.6.4 regra 1). Sem animação na
 * primeira montagem, no replay, com movimento reduzido (reativo, §15.6) ou com a aba oculta. A reordenação não move o
 * foco: o item focado continua focado na nova posição.
 */
export function FlipList<T extends { key: FlipKey }>({
  items,
  renderItem,
  as = 'ul',
  freezeOnHover = false,
  replay = false,
  focusFallback,
  class: extra,
  itemClass,
  ariaLabel,
  ariaLabelledby,
}: FlipListProps<T>) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const [hover, setHover] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  /** Ordem que está no DOM (gravada depois de cada commit). */
  const shown = useRef<FlipKey[] | null>(null);
  const before = useRef<Map<string, Point> | null>(null);
  const focused = useRef<HTMLElement | null>(null);
  const running = useRef(new Map<string, Animation>());

  const byKey = new Map(items.map((it) => [it.key, it] as const));
  const nextKeys = items.map((it) => it.key);
  const frozen = freezeOnHover && (hover || focusWithin);
  const keys = frozen && shown.current ? mergeFrozenOrder(shown.current, nextKeys) : nextKeys;

  // Antes do diff (o DOM ainda tem a ordem velha): posições visíveis, com as transformações em curso, e o foco.
  const list = ref.current;
  if (list && shown.current && !sameOrder(shown.current, keys)) {
    const animate = !reduced && !replay && !isTabHidden();
    before.current = animate ? positions(list) : null;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    focused.current = active instanceof HTMLElement && active !== list && list.contains(active) ? active : null;
  }

  useLayoutEffect(() => {
    const el = ref.current;
    const prev = shown.current;
    shown.current = keys;
    const snap = before.current;
    before.current = null;
    const f = focused.current;
    focused.current = null;
    if (!el) return;

    // §3.9 e §15.2 item 4: o foco fica no mesmo item; se ele saiu, vai para o título da lista.
    if (f) {
      if (f.isConnected) {
        if (document.activeElement !== f) f.focus({ preventScroll: true });
      } else focusFallback?.current?.focus({ preventScroll: true });
    }

    if (reduced || replay) {
      for (const a of running.current.values()) a.cancel();
      running.current.clear();
    }
    if (!snap || !prev) return;

    // Interrompe o que está correndo: a posição de partida já foi medida com a transformação aplicada.
    for (const a of running.current.values()) a.cancel();
    running.current.clear();
    const after = positions(el);
    const prevSet = new Set(prev.map(String));
    const moveEase = easing(el, '--ease-io', 'ease-in-out');
    const enterEase = easing(el, '--ease-out', 'ease-out');
    for (const child of Array.from(el.children) as HTMLElement[]) {
      const key = child.dataset.key;
      if (key === undefined || typeof child.animate !== 'function') continue;
      const from = snap.get(key);
      const to = after.get(key);
      let anim: Animation | null = null;
      if (from && to) {
        const dx = from.x - to.x;
        const dy = from.y - to.y;
        if (Math.abs(dx) >= 0.5 || Math.abs(dy) >= 0.5) {
          anim = child.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
            duration: FLIP_MOVE_MS,
            easing: moveEase,
          });
        }
      } else if (!prevSet.has(key)) {
        anim = child.animate(
          [
            { opacity: 0, transform: `translateY(${FLIP_ENTER_OFFSET_PX}px)` },
            { opacity: 1, transform: 'none' },
          ],
          { duration: FLIP_ENTER_MS, delay: FLIP_ENTER_DELAY_MS, easing: enterEase, fill: 'backwards' },
        );
      }
      if (anim) {
        const mine = anim;
        // cancel() rejeita `finished` com AbortError; o navegador marca essa rejeição como tratada (Web Animations,
        // "cancel an animation"), o happy-dom não: sem o catch, cada interrupção vira rejeição não tratada nos testes
        mine.finished?.catch(() => undefined);
        running.current.set(key, mine);
        mine.onfinish = () => {
          if (running.current.get(key) === mine) running.current.delete(key);
        };
      }
    }
  });

  const freezeHandlers = freezeOnHover
    ? {
        onPointerEnter: () => setHover(true),
        onPointerLeave: () => setHover(false),
        onFocusIn: () => setFocusWithin(true),
        onFocusOut: (e: FocusEvent) => {
          const next = e.relatedTarget;
          if (!(next instanceof Node && ref.current?.contains(next))) setFocusWithin(false);
        },
      }
    : {};

  const Tag = as;
  const Item = as === 'div' ? 'div' : 'li';
  const itemCls = itemClass ? `flip-list__item ${itemClass}` : 'flip-list__item';
  return (
    <Tag
      // biome-ignore lint/suspicious/noExplicitAny: o elemento varia entre ol, ul e div; só a medida do DOM é usada
      ref={ref as any}
      class={extra ? `flip-list ${extra}` : 'flip-list'}
      data-testid="flip-list"
      data-state={frozen ? 'frozen' : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      {...freezeHandlers}
    >
      {keys.map((k, i) => {
        const item = byKey.get(k) as T;
        return (
          <Item key={k} class={itemCls} data-key={String(k)}>
            {renderItem(item, i)}
          </Item>
        );
      })}
    </Tag>
  );
}
